import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; 
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../api';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../apiConfig';
import './Friends.css';
import { commonIcons } from './assetImports';

const FriendsPage = ({ wallets, setWallets, isTelegramMiniApp }) => {
    const { t } = useTranslation('friends');
    const { user, socket } = useContext(AuthContext);
    const [friends, setFriends] = useState([]);
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [totalTokenEarnings, setTotalTokenEarnings] = useState(0);
    const [isClaiming, setIsClaiming] = useState(false);
    const [claimSuccessPopup, setClaimSuccessPopup] = useState({ isVisible: false });
    const navigate = useNavigate();
    const currency = isTelegramMiniApp ? 'stars' : 'usdt';
    const [telegramInviteLink, setTelegramInviteLink] = useState('');
    const [fallbackInviteLink, setFallbackInviteLink] = useState('');
    const [showFriendsListPopup, setShowFriendsListPopup] = useState(false);
    const [invitedFriends, setInvitedFriends] = useState([]);


    const handleGetInviteLink = async () => {
        try {
            const telegramInviteLink =
                `https://t.me/TG_Game_stars_bot?start=${user._id}`;
            const fallbackInviteLink =
                `https://gamestar24.com/register?invitedBy=${user._id}`;

            setTelegramInviteLink(telegramInviteLink);
            setFallbackInviteLink(fallbackInviteLink);
                
       
        } catch (error) {
          console.error('Error generating invite link:', error);
        }
      };
      

      useEffect(() => {
        if (user) {
          handleGetInviteLink();
        }
      }, [user]);
      

    useEffect(() => {
        const fetchFriends = async () => {
          try {
            const response = await apiRequest(`${API_BASE_URL}/api/friends?currency=${currency}`, {
              method: 'GET',
              credentials: 'include',
            });
            const data = await response.json();
            if (response.ok) {
              setFriends(data.friends);
              setTotalEarnings(data.totalRewards);
              setTotalTokenEarnings(data.totalTokenRewards || 0);
              setInvitedFriends(data.invitedFriends || []);
            }
          } catch (error) {
            console.error('Error fetching friends:', error);
          }
        };
        fetchFriends();
      }, [currency]);
      

    useEffect(() => {
        if (socket) {
            socket.on('friendAdded', (newFriend) => {
                setFriends(prev => [...prev, newFriend]);
            });
            return () => socket.off('friendAdded');
        }
    }, [socket]);

    const handleClaimEarnings = async () => {
        if (totalEarnings <= 0) return;
        
        setIsClaiming(true);
        try {
            const response = await apiRequest(`${API_BASE_URL}/api/claim-rewards`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ currency: currency })
            });
            
            const data = await response.json();
            if (response.ok) {
                triggerConfettiAnimation();
                setWallets(data.newWallets);
                setTotalEarnings(0);
                setFriends(prev => prev.map(f => ({ ...f, earnings: 0 })));
                
                setClaimSuccessPopup({
                    isVisible: true,
                    isError: false,
                    amount: totalEarnings,
                    currency
                  });
            }
        } catch (error) {
            console.error('Error claiming rewards:', error);
            setClaimSuccessPopup({
                isVisible: true,
                isError: true,
                message: "Something went wrong."
              });
        } finally {
            setIsClaiming(false);
        }
    };

    const handleClaimTokenEarnings = async () => {
  if (totalTokenEarnings <= 0) return;
  
  setIsClaiming(true);
  try {
    const response = await apiRequest(`${API_BASE_URL}/api/claim-rewards`, {
      method: 'POST',
      credentials: 'include',
      headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ currency: 'token' })
    });
    
    const data = await response.json();
    if (response.ok) {
      triggerConfettiAnimation();
      setWallets(data.newWallets);
      setTotalTokenEarnings(0);
      setFriends(prev => prev.map(f => ({ ...f, tokenEarnings: 0 })));
      
      setClaimSuccessPopup({
        isVisible: true,
        isError: false,
        amount: totalTokenEarnings,
        currency: 'token'
      });
    }
  } catch (error) {
    console.error('Error claiming token rewards:', error);
    setClaimSuccessPopup({
      isVisible: true,
      isError: true,
      message: "Something went wrong."
    });
  } finally {
    setIsClaiming(false);
  }
};

    const triggerConfettiAnimation = () => {
        const confettiContainer = document.createElement('div');
        confettiContainer.className = 'friends-confetti-container';
    
        for (let i = 0; i < 200; i++) {
            const confetti = document.createElement('div');
            confetti.className = 'friends-confetti-piece';
            
            confetti.style.left = '50%';
            confetti.style.top = '50%';
            confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            confetti.style.transform = `rotate(${Math.random() * 360}deg)`;
    
            const randomX = (Math.random() - 0.5) * window.innerWidth * 2;
            const randomY = (Math.random() - 0.5) * window.innerHeight * 2;
            const duration = Math.random() * 1.4 + 0.8;
    
            confetti.style.animation = `friends-explode ${duration}s ease-out forwards`;
            confetti.style.setProperty('--randomX', `${randomX}px`);
            confetti.style.setProperty('--randomY', `${randomY}px`);
    
            confettiContainer.appendChild(confetti);
        }
    
        document.body.appendChild(confettiContainer);
    
        setTimeout(() => {
            document.body.removeChild(confettiContainer);
        }, 4000);
    };

    const handleInviteFriends = () => {
      const tg = window.Telegram?.WebApp;
        if (tg.initData) {
          const telegramShareLink = `https://t.me/share/url?url=${encodeURIComponent(telegramInviteLink)}&text=${encodeURIComponent(t('share_text'))}`;
          window.open(telegramShareLink, '_blank');
        } else {
          if (navigator.share) {
            navigator.share({
              title: 'Join me!',
              text: "Let's play-to-earn together — join me now!",
              url: fallbackInviteLink
            }).catch(console.error);
          } else {
            navigator.clipboard.writeText(fallbackInviteLink).then(() => {
              alert(t('alerts.link_copied'));
            });
          }
        }
      };
      
      const copyInviteLink = () => {
        const tg = window.Telegram?.WebApp;
        const linkToCopy = tg.initData ? telegramInviteLink : fallbackInviteLink;
        navigator.clipboard.writeText(linkToCopy).then(() => {
           alert(t('alerts.link_copied'));
        });
      };
      
      

  const getCurrencyIcon = (currency) => {
  switch (currency) {
    case 'stars':
      return <img src={commonIcons.starIcon} alt="Stars" className="friends-currency-icon friends-currency-icon-stars" />;
    case 'token':
      return <img src={commonIcons.tokenYellow} alt="Token" className="friends-currency-icon friends-currency-icon-token" />;
    default:
      return <img src={commonIcons.usdtIcon} alt="USDT" className="friends-currency-icon friends-currency-icon-usdt" />;
  }
};


const handleShowFriendsList = () => {
  if (user?.invitedFriends?.length > 0) {
    setShowFriendsListPopup(true);
  }
};

const handleCloseFriendsList = () => {
  setShowFriendsListPopup(false);
};

    return (
        <div className="friends-page-container">
            {/* Success Popup */}
            {claimSuccessPopup.isVisible && (
  <>
    <div className="friends-popup-overlay" onClick={() => setClaimSuccessPopup({ isVisible: false })}></div>

    <div className="friends-reward-popup" onClick={(e) => e.stopPropagation()}>
      <div className="friends-screen-blur"></div>
      <div className="friends-popup-background"></div>

      <div className="friends-popup-content">
        {claimSuccessPopup.isError ? (
          <>
            <div className="friends-popup-error-message">{t('popup.error')}</div>
            <button className="friends-close-btn" onClick={() => setClaimSuccessPopup({ isVisible: false })}>
              {t('popup.close')}
            </button>
          </>
        ) : (
          <>
            <h3 className="friends-popup-title">{t('popup.claimed')}</h3>

            <div className="friends-points-center">
              <span className="friends-points-value">+{claimSuccessPopup.amount}</span>
              <div
                className="friends-reward-icon"
                style={{
                  backgroundImage:
                    claimSuccessPopup.currency === 'stars'
                       ? `url(${commonIcons.starIcon})`
                      : claimSuccessPopup.currency === 'token'
                       ? `url(${commonIcons.tokenYellow})`
                      : `url(${commonIcons.usdtIcon})`,
                }}
              />
            </div>

            <button className="friends-close-btn" onClick={() => setClaimSuccessPopup({ isVisible: false })}>
              {t('popup.close')}
            </button>
          </>
        )}
      </div>
    </div>
  </>
)}


           <h1 className="friends-title">
  {t('title_part1')}<br />
  {t('title_part2_pre')} <span className="friends-highlight-percentage">{user?.affiliatePercentage || 4}%</span> {t('title_part2_post')}
</h1>
            
            <p className="friends-subtitle">{t('subtitle')}</p>

            <div className="friends-stats-container">
                <div 
  className="base-item-box friends-item-box friends-clickable" 
  onClick={handleShowFriendsList}
>
                    <div className="friends-item-content">
                        <div className="friends-item-info">
                            <h4 className="friends-item-name">{t('stats.invited_friends')}</h4>
                            <p className="friends-item-value">{user?.invitedFriends.length || 0}</p>
                        </div>
                    </div>
                </div>

                  <div 
    className={`base-item-box friends-item-box ${totalTokenEarnings > 0 ? 'friends-clickable' : ''}`} 
    onClick={totalTokenEarnings > 0 ? handleClaimTokenEarnings : null}
  >
    <div className="friends-item-content">
      <div className="friends-item-info">
        <h4 className="friends-item-name">
          {t('stats.earned')} {getCurrencyIcon('token')}
        </h4>
        <p className={`friends-item-value ${totalTokenEarnings === 0 ? 'friends-zero-earnings' : ''}`}>
          {totalTokenEarnings.toFixed(0)}
        </p>
      </div>
    </div>
  </div>

                
                
                <div 
                    className={`base-item-box friends-item-box ${totalEarnings > 0 ? 'friends-clickable' : ''}`} 
                    onClick={totalEarnings > 0 ? handleClaimEarnings : null}
                >
                    <div className="friends-item-content">
                        <div className="friends-item-info">
                            <h4 className="friends-item-name">
  {t('stats.earned')} {getCurrencyIcon(currency)}
</h4>
                            <p className={`friends-item-value ${totalEarnings === 0 ? 'friends-zero-earnings' : ''}`}>
                                {totalEarnings.toFixed(currency === 'usdt' ? 2 : 0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

           


            <div className="friends-buttons-container">
  <button 
    className="base-item-box friends-invite-button" 
    onClick={handleInviteFriends}
  >
    {t('buttons.invite')}
  </button>
  <button 
    className="base-item-box friends-copy-button" 
    onClick={copyInviteLink}
  >
    <img src={commonIcons.copyIcon} alt="Copy link" />
  </button>
</div>

{friends.some(f => f.earnings > 0 || f.tokenEarnings > 0) && (
  <div className="friends-earnings-list">
    <h3 className="friends-earnings-title">{t('earnings.recent')}</h3>
    {friends.map(friend => (
   
      ((friend.earnings > 0 || friend.tokenEarnings > 0) && (
        <div key={friend._id} className="friends-earning-item earning-item-animate-in">
          <div className="friends-earning-avatar">
            {friend.avatarUrl ? (
              <img src={friend.avatarUrl} alt={friend.username} />
            ) : (
              <div className="friends-earning-initial">{friend.username[0].toUpperCase()}</div>
            )}
          </div>
          <div className="friends-earning-info">
            <p className="friends-earning-name">{friend.username}</p>
            <p className="friends-earning-date">
        
              {friend.lastEarningDate || friend.lastTokenEarningDate 
                ? new Date(Math.max(
                    new Date(friend.lastEarningDate || 0).getTime(),
                    new Date(friend.lastTokenEarningDate || 0).getTime()
                  )).toLocaleString()
                : t('earnings.no_date')}
            </p>
          </div>
          <div className="friends-earning-amounts">
         
            {friend.earnings > 0 && (
              <div className="friends-earning-amount">
                +{friend.earnings.toFixed(currency === 'usdt' ? 2 : 0)} {getCurrencyIcon(currency)}
              </div>
            )}
        
            {friend.tokenEarnings > 0 && (
              <div className="friends-earning-amount">
                +{friend.tokenEarnings.toFixed(0)} {getCurrencyIcon('token')}
              </div>
            )}
          </div>
        </div>
      ))
    ))}
  </div>
)}


{showFriendsListPopup && (
  <div className="friends-list-popup-overlay" onClick={handleCloseFriendsList}>
    <div className="friends-list-popup" onClick={(e) => e.stopPropagation()}>
      
        <div className="friends-list-header">
          <h3 className="friends-list-title">{t('friends_list.title')}</h3>
          <button className="friends-list-close-btn" onClick={handleCloseFriendsList}>
            {t('friends_list.close')}
          </button>
        </div>
        
        <div className="friends-list-scroll-container">
          <div className="friends-list-body">
            {invitedFriends.map(friend => (
              <div key={friend._id} className="friends-list-item">
                <div className="friends-list-avatar">
                  {friend.photoUrl ? (
                    <img src={friend.photoUrl} alt={friend.nickname} />
                  ) : (
                    <div className="friends-list-initial">
                      {(friend.nickname || 'F')[0].toUpperCase()}
                    </div>
                  )}
                </div>
                
                <div className="friends-list-info">
                  <p className="friends-list-name">{friend.nickname || 'Friend'}</p>
                  <p className="friends-list-date">
                    {friend.invitedAt 
                      ? new Date(friend.invitedAt).toLocaleDateString() 
                      : t('friends_list.no_date')}
                  </p>
                </div>
                
                <div className="friends-list-status">
                  {friend.hasPlayed ? t('friends_list.played') : t('friends_list.invited')}
                </div>
              </div>
            ))}
          </div>
        </div>
      
    </div>
  </div>
)}

        </div>
    );
};

export default FriendsPage;